package com.daytimegaming.ourdailyfinances.data.repository

import com.daytimegaming.ourdailyfinances.data.network.dto.CreateRealEstateHoldingRequest
import com.daytimegaming.ourdailyfinances.data.network.dto.UpdateRealEstateHoldingRequest
import com.daytimegaming.ourdailyfinances.data.network.service.RealEstateService
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.RealEstateHolding
import com.daytimegaming.ourdailyfinances.domain.repository.RealEstateRepository
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow

class RealEstateRepositoryImpl(private val service: RealEstateService) : RealEstateRepository {

    override suspend fun createRealEstateHolding(
        nickname: String,
        addressLine1: String,
        addressLine2: String?,
        city: String,
        state: String,
        zipCode: String,
        county: String?,
        purchasePrice: Double,
        mortgageAmount: Double?,
        mortgageInterestRate: Double?
    ): Response<RealEstateHolding> {
        return try {
            val request = CreateRealEstateHoldingRequest(
                nickname = nickname,
                addressLine1 = addressLine1,
                addressLine2 = addressLine2,
                city = city,
                state = state,
                zipCode = zipCode,
                county = county,
                purchasePrice = purchasePrice,
                mortgageAmount = mortgageAmount,
                mortgageInterestRate = mortgageInterestRate
            )
            val dto = service.createRealEstateHolding(request)
            Response.Success(dto.toDomain())
        } catch (e: Exception) {
            Response.Error(e.message ?: "Failed to create real estate holding")
        }
    }

    override fun getRealEstateHoldings(): Flow<Response<List<RealEstateHolding>>> = flow {
        emit(Response.Loading)
        try {
            val response = service.getRealEstateHoldings()
            val list = response.holdings.map { it.toDomain() }
            emit(Response.Success(list))
        } catch (e: Exception) {
            emit(Response.Error(e.message ?: "Failed to load real estate holdings"))
        }
    }

    override fun getRealEstateHolding(realEstateHoldingId: String): Flow<Response<RealEstateHolding>> = flow {
        emit(Response.Loading)
        try {
            val dto = service.getRealEstateHolding(realEstateHoldingId)
            emit(Response.Success(dto.toDomain()))
        } catch (e: Exception) {
            emit(Response.Error(e.message ?: "Failed to load real estate holding"))
        }
    }

    override suspend fun updateRealEstateHolding(
        realEstateHoldingId: String,
        nickname: String?,
        addressLine1: String?,
        addressLine2: String?,
        city: String?,
        state: String?,
        zipCode: String?,
        county: String?,
        purchasePrice: Double?,
        mortgageAmount: Double?,
        mortgageInterestRate: Double?
    ): Response<RealEstateHolding> {
        return try {
            val request = UpdateRealEstateHoldingRequest(
                nickname = nickname,
                addressLine1 = addressLine1,
                addressLine2 = addressLine2,
                city = city,
                state = state,
                zipCode = zipCode,
                county = county,
                purchasePrice = purchasePrice,
                mortgageAmount = mortgageAmount,
                mortgageInterestRate = mortgageInterestRate
            )
            val dto = service.updateRealEstateHolding(realEstateHoldingId, request)
            Response.Success(dto.toDomain())
        } catch (e: Exception) {
            Response.Error(e.message ?: "Failed to update real estate holding")
        }
    }

    override suspend fun deleteRealEstateHolding(realEstateHoldingId: String): Response<Unit> {
        return try {
            service.deleteRealEstateHolding(realEstateHoldingId)
            Response.Success(Unit)
        } catch (e: Exception) {
            Response.Error(e.message ?: "Failed to delete real estate holding")
        }
    }

    override suspend fun refreshValueEstimate(realEstateHoldingId: String): Response<RealEstateHolding> {
        return try {
            val dto = service.refreshValueEstimate(realEstateHoldingId)
            Response.Success(dto.toDomain())
        } catch (e: Exception) {
            Response.Error(e.message ?: "Failed to refresh value estimate")
        }
    }
}
